import { Fragment } from 'react';
import DOMPurify from 'dompurify';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DroppableEmptyZone, useDraggableDroppable } from '@components/DragAndDrop';
import { BLOCK_ICONS } from './BlockPalette';
import { BLOCK_LABEL_KEYS } from './designUtils';
import Tooltip from '@components/Tooltip';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { EMAIL_BLOCK_TYPE } from '@constants/campaignTemplates';
import { PLACEHOLDER_CONTEXT, createPlaceholderResolver } from '@constants/placeholders';
import { escapeRegex, serializePlaceholdersToHtml } from '@helpers/text-editor';

const CanvasWrapper = styled('div')(({ bg }) => ({
    flex: 1,
    overflowY: 'auto',
    padding: '24px 16px',
    backgroundColor: bg,
    display: 'flex',
    justifyContent: 'center',
    '& .pl-canvas-tokens mark.pl-custom-highlight': {
        backgroundColor: '#bcc9ce',
        borderRadius: 8,
        padding: '0 4px',
        color: 'inherit',
        fontSize: '0.85em',
    },
    '& .pl-canvas-tokens p': {
        margin: 0,
    },
}));

const EmailSheet = styled('div')({
    width: 600,
    maxWidth: '100%',
});

const ContentCard = styled('div', {
    shouldForwardProp: prop => prop !== 'contentBg' && prop !== 'expanded',
})(({ contentBg, expanded }) => ({
    backgroundColor: contentBg,
    borderRadius: 4,
    padding: '8px 0',
    paddingBottom: expanded ? 8 + DROP_GAP : 8,
    transition: 'padding-bottom 0.15s ease',
}));

const BlockShell = styled('div', {
    shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
    position: 'relative',
    cursor: 'pointer',
    outline: isSelected ? '2px solid var(--primary-main)' : '2px solid transparent',
    outlineOffset: -2,
    borderRadius: 4,
    '&:hover': {
        outline: isSelected ? '2px solid var(--primary-main)' : '2px dashed var(--primary-main)',
    },
    '&:hover .pl-block-toolbar': {
        opacity: 1,
    },
}));

const DragHandle = styled('span')({
    display: 'flex',
    alignItems: 'center',
    padding: '4px 2px',
    cursor: 'grab',
    touchAction: 'none',
    '& svg': {
        width: 18,
        height: 18,
        color: 'var(--text-secondary)',
    },
    '&:hover svg': {
        color: 'var(--text-primary)',
    },
});

const BlockToolbar = styled('div')({
    position: 'absolute',
    top: 4,
    right: 4,
    display: 'flex',
    gap: 2,
    background: '#fff',
    borderRadius: 6,
    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
    opacity: 0,
    transition: 'opacity 0.15s',
    zIndex: 2,
});

const FooterPreview = styled('div')({
    marginTop: 16,
    padding: '12px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    textAlign: 'center',
    opacity: 0.8,
});

const fallbackChipLabel = (t, label, fallback) => `${label} ${t('common.or')} "${fallback}"`;

// Plain-text values (heading, button label): non-token text is HTML-escaped by the serializer.
const tokenHtml = (t, value) => {
    const resolver = createPlaceholderResolver(t, PLACEHOLDER_CONTEXT.CAMPAIGN);
    return {
        __html: serializePlaceholdersToHtml(value || '', {
            ...resolver,
            allowFallback: true,
            formatFallback: (label, fallback) => fallbackChipLabel(t, label, fallback),
        }),
    };
};

// Rich-text block value is HTML already: sanitize it, then decorate token markers as chips.
const richTextHtml = (t, html) => {
    const resolver = createPlaceholderResolver(t, PLACEHOLDER_CONTEXT.CAMPAIGN);
    const clean = DOMPurify.sanitize(html || '', {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'span', 'ul', 'ol', 'li', 'a'],
        ALLOWED_ATTR: ['href'],
    });
    if (!resolver.tokens.length) return { __html: clean };
    const tokenRegex = new RegExp(`\\[(${resolver.tokens.map(escapeRegex).join('|')})(?:\\|([^\\]]*))?\\]`, 'g');
    return {
        __html: clean.replace(tokenRegex, (_, token, fallback) => {
            const label = fallback
                ? fallbackChipLabel(t, resolver.getLabel(token), fallback)
                : resolver.getLabel(token);
            return `<mark class="pl-custom-highlight">${label}</mark>`;
        }),
    };
};

const PADDING_PX = { s: 8, m: 16, l: 24 };

const BlockPreview = ({ block, styles, brandLogoUrl, t }) => {
    const align = block.align || 'center';
    const font = `${styles.font}, sans-serif`;
    const pad = PADDING_PX[block.padding] || 16;

    switch (block.type) {
        case EMAIL_BLOCK_TYPE.LOGO: {
            const logoUrl = block.url || brandLogoUrl;
            return (
                <div style={{ padding: `${pad}px 20px`, textAlign: align }}>
                    {logoUrl ? (
                        <img src={logoUrl} alt="" style={{ maxHeight: 60, maxWidth: '100%' }} />
                    ) : (
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                            {t('TemplateLibrary.logoPlaceholder')}
                        </Typography>
                    )}
                </div>
            );
        }
        case EMAIL_BLOCK_TYPE.IMAGE:
            return block.url ? (
                <div style={{ padding: `${pad}px 20px`, textAlign: align }}>
                    <img
                        src={block.url}
                        alt={block.alt || ''}
                        style={{
                            maxWidth: '100%',
                            width: block.widthPercent ? `${block.widthPercent}%` : 'auto',
                            borderRadius: block.radius || 0,
                        }}
                    />
                </div>
            ) : (
                <div
                    style={{
                        margin: `${pad}px 20px`,
                        padding: '40px 0',
                        textAlign: 'center',
                        border: '1px dashed rgba(0,0,0,0.25)',
                        borderRadius: 4,
                        color: 'var(--text-secondary)',
                    }}
                >
                    {t('TemplateLibrary.imagePlaceholder')}
                </div>
            );
        case EMAIL_BLOCK_TYPE.HEADING: {
            const level = Math.min(Math.max(block.level || 1, 1), 3);
            const HeadingTag = `h${level}`;
            return (
                <HeadingTag
                    className="pl-canvas-tokens"
                    style={{
                        margin: 0,
                        padding: `${pad}px 20px`,
                        fontFamily: font,
                        fontSize: { 1: 26, 2: 22, 3: 18 }[level],
                        lineHeight: 1.3,
                        color: block.color || styles.textColor,
                        textAlign: align,
                    }}
                    dangerouslySetInnerHTML={tokenHtml(t, block.text || t('TemplateLibrary.headingPlaceholder'))}
                />
            );
        }
        case EMAIL_BLOCK_TYPE.TEXT:
            return (
                <div
                    className="pl-canvas-tokens"
                    style={{
                        padding: `${pad}px 20px`,
                        fontFamily: font,
                        fontSize: block.size === 's' ? 14 : block.size === 'l' ? 18 : 16,
                        lineHeight: 1.5,
                        color: block.color || styles.textColor,
                        textAlign: align,
                    }}
                    dangerouslySetInnerHTML={richTextHtml(
                        t,
                        block.html || `<p>${t('TemplateLibrary.textPlaceholder')}</p>`,
                    )}
                />
            );
        case EMAIL_BLOCK_TYPE.BUTTON: {
            const shape = block.shape || styles.buttonShape;
            const radius = shape === 'square' ? 0 : shape === 'pill' ? 24 : 8;
            const isAuto = block.width === 'auto';
            const fontSize = block.size === 's' ? 14 : block.size === 'l' ? 20 : 18;
            const paddingY = block.size === 's' ? 8 : block.size === 'l' ? 16 : 12;
            return (
                <div style={{ padding: `${pad}px 20px`, textAlign: align }}>
                    <span
                        className="pl-canvas-tokens"
                        style={{
                            display: isAuto ? 'inline-block' : 'block',
                            backgroundColor: block.color || styles.buttonColor,
                            color: block.textColor || '#fff',
                            borderRadius: radius,
                            padding: `${paddingY}px ${isAuto ? 32 : 0}px`,
                            fontFamily: font,
                            fontWeight: 'bold',
                            fontSize,
                            textAlign: 'center',
                        }}
                        dangerouslySetInnerHTML={tokenHtml(t, block.label || t('TemplateLibrary.buttonPlaceholder'))}
                    />
                </div>
            );
        }
        case EMAIL_BLOCK_TYPE.DIVIDER:
            return (
                <div style={{ padding: `${pad}px 20px` }}>
                    <div style={{ borderTop: `${block.height || 1}px solid ${block.color || '#E0E0E0'}` }} />
                </div>
            );
        case EMAIL_BLOCK_TYPE.SPACER:
            return <div style={{ height: block.height || 24 }} />;
        case EMAIL_BLOCK_TYPE.SOCIAL:
            return (
                <div style={{ padding: `${pad}px 20px`, textAlign: align }}>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                        {t('TemplateLibrary.socialPlaceholder')}
                    </Typography>
                </div>
            );
        default:
            return null;
    }
};

export const CANVAS_DROP_ZONE_ID = 'pl-canvas-drop-zone';
export const COLUMN_ZONE_PREFIX = 'colzone:';

export const DROP_GAP = 56;

const DropSlotWrapper = styled('div')({
    height: 0,
    position: 'relative',
    zIndex: 2,
});

const DropSlotBox = styled('div')({
    position: 'absolute',
    top: 4,
    left: 12,
    right: 12,
    height: DROP_GAP - 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 8,
    border: '2px dashed var(--primary-main)',
    background: '#eef2ff',
    color: 'var(--primary-main)',
    fontSize: 13,
    fontFamily: 'Rubik, sans-serif',
    pointerEvents: 'none',
    '& svg': {
        width: 18,
        height: 18,
    },
});

const DropSlot = ({ dragType, t }) => {
    const Icon = dragType ? BLOCK_ICONS[dragType] : null;
    return (
        <DropSlotWrapper>
            <DropSlotBox>
                {Icon && <Icon />}
                {dragType ? t(BLOCK_LABEL_KEYS[dragType]) : null}
            </DropSlotBox>
        </DropSlotWrapper>
    );
};

export const columnZoneId = (blockId, side) => `${COLUMN_ZONE_PREFIX}${blockId}:${side}`;

const BlockActions = ({ listeners, attributes, id, onDuplicate, onRemove }) => (
    <BlockToolbar className="pl-block-toolbar">
        <DragHandle {...listeners} {...attributes} onClick={e => e.stopPropagation()}>
            <DragIndicatorIcon />
        </DragHandle>
        <IconButton
            size="small"
            onClick={e => {
                e.stopPropagation();
                onDuplicate(id);
            }}
        >
            <ContentCopyIcon fontSize="inherit" />
        </IconButton>
        <IconButton
            size="small"
            onClick={e => {
                e.stopPropagation();
                onRemove(id);
            }}
        >
            <DeleteIcon fontSize="inherit" />
        </IconButton>
    </BlockToolbar>
);

const ColumnChild = ({ block, styles, isSelected, isView, shifted, blockProps, t }) => {
    const { setRefs, attributes, listeners, isDragging } = useDraggableDroppable(block.id);
    return (
        <BlockShell
            ref={setRefs}
            isSelected={isSelected}
            style={{
                opacity: isDragging ? 0.4 : 1,
                transform: shifted ? `translateY(${DROP_GAP}px)` : 'none',
                transition: 'transform 0.15s ease',
            }}
            onClick={e => {
                e.stopPropagation();
                blockProps.onSelect(block.id);
            }}
        >
            {!isView && (
                <BlockActions
                    listeners={listeners}
                    attributes={attributes}
                    id={block.id}
                    onDuplicate={blockProps.onDuplicate}
                    onRemove={blockProps.onRemove}
                />
            )}
            <BlockPreview block={block} styles={styles} brandLogoUrl={blockProps.brandLogoUrl} t={t} />
        </BlockShell>
    );
};

const ColumnCell = ({ block, side, styles, isView, blockProps, t }) => {
    const children = block[side] || [];
    const { dropTarget } = blockProps;
    const indicatorAt = index =>
        dropTarget && dropTarget.containerId === block.id && dropTarget.side === side && dropTarget.index === index;

    const shiftFrom =
        dropTarget && dropTarget.containerId === block.id && dropTarget.side === side ? dropTarget.index : null;

    return (
        <div style={{ flex: 1, minWidth: 0 }}>
            {children.map((child, index) => (
                <Fragment key={child.id}>
                    {indicatorAt(index) && <DropSlot dragType={blockProps.dragType} t={t} />}
                    <ColumnChild
                        block={child}
                        styles={styles}
                        isSelected={blockProps.selectedBlockId === child.id}
                        isView={isView}
                        shifted={shiftFrom !== null && index >= shiftFrom}
                        blockProps={blockProps}
                        t={t}
                    />
                </Fragment>
            ))}
            {indicatorAt(children.length) && <DropSlot dragType={blockProps.dragType} t={t} />}
            {!isView &&
                (children.length === 0 || (children.length === 1 && children[0].id === blockProps.dragBlockId)) && (
                    <DroppableEmptyZone
                        id={columnZoneId(block.id, side)}
                        label={t('TemplateLibrary.dropHint')}
                        style={{ minHeight: 48, margin: 4 }}
                    />
                )}
        </div>
    );
};

const CanvasBlock = ({ block, styles, brandLogoUrl, isView, shifted, blockProps, t }) => {
    const { setRefs, attributes, listeners, isDragging } = useDraggableDroppable(block.id);
    return (
        <BlockShell
            ref={setRefs}
            isSelected={blockProps.selectedBlockId === block.id}
            style={{
                opacity: isDragging ? 0.4 : 1,
                transform: shifted ? `translateY(${DROP_GAP}px)` : 'none',
                transition: 'transform 0.15s ease',
            }}
            onClick={() => blockProps.onSelect(block.id)}
        >
            {!isView && (
                <BlockActions
                    listeners={listeners}
                    attributes={attributes}
                    id={block.id}
                    onDuplicate={blockProps.onDuplicate}
                    onRemove={blockProps.onRemove}
                />
            )}
            {block.type === EMAIL_BLOCK_TYPE.COLUMNS ? (
                <div style={{ display: 'flex', gap: 12, padding: `${PADDING_PX[block.padding] || 16}px 20px` }}>
                    <ColumnCell
                        block={block}
                        side="left"
                        styles={styles}
                        isView={isView}
                        blockProps={blockProps}
                        t={t}
                    />
                    <ColumnCell
                        block={block}
                        side="right"
                        styles={styles}
                        isView={isView}
                        blockProps={blockProps}
                        t={t}
                    />
                </div>
            ) : (
                <BlockPreview block={block} styles={styles} brandLogoUrl={brandLogoUrl} t={t} />
            )}
        </BlockShell>
    );
};

const BlockCanvas = ({
    design,
    selectedBlockId,
    onSelectBlock,
    onDuplicate,
    onRemove,
    isView,
    brandLogoUrl,
    dropTarget,
    dragType,
    dragBlockId,
}) => {
    const { t } = useTranslation();
    const { styles, blocks } = design;

    const blockProps = {
        selectedBlockId,
        onSelect: onSelectBlock,
        onDuplicate,
        onRemove,
        dropTarget,
        dragType,
        dragBlockId,
        brandLogoUrl,
    };

    const topIndicatorAt = index => dropTarget && dropTarget.containerId === null && dropTarget.index === index;

    const containerIndex = dropTarget?.containerId
        ? blocks.findIndex(block => block.id === dropTarget.containerId)
        : -1;
    const topShiftFrom = !dropTarget
        ? null
        : dropTarget.containerId === null
          ? dropTarget.index
          : containerIndex === -1
            ? null
            : containerIndex + 1;

    return (
        <CanvasWrapper bg={styles.bg}>
            <EmailSheet>
                <ContentCard contentBg={styles.contentBg} expanded={!!dropTarget}>
                    {blocks.map((block, index) => (
                        <Fragment key={block.id}>
                            {topIndicatorAt(index) && <DropSlot dragType={dragType} t={t} />}
                            <CanvasBlock
                                block={block}
                                styles={styles}
                                brandLogoUrl={brandLogoUrl}
                                isView={isView}
                                shifted={topShiftFrom !== null && index >= topShiftFrom}
                                blockProps={blockProps}
                                t={t}
                            />
                        </Fragment>
                    ))}
                    {topIndicatorAt(blocks.length) && <DropSlot dragType={dragType} t={t} />}
                    {!isView && blocks.length === 0 && (
                        <DroppableEmptyZone
                            id={CANVAS_DROP_ZONE_ID}
                            label={t('TemplateLibrary.dropHint')}
                            style={{ margin: '8px 20px' }}
                        />
                    )}
                </ContentCard>
                <FooterPreview>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                        {t('TemplateLibrary.footerPreviewInfo')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', textDecoration: 'underline' }}>
                        {t('TemplateLibrary.footerPreviewLinks')}
                    </Typography>
                    <Tooltip title={t('TemplateLibrary.footerLockedTooltip')} placement="top">
                        <LockIcon sx={{ fontSize: 14, color: 'var(--text-tertiary, rgba(0,0,0,0.3))' }} />
                    </Tooltip>
                </FooterPreview>
            </EmailSheet>
        </CanvasWrapper>
    );
};

export default BlockCanvas;
